import React, { useMemo } from "react";
import { SendOutlined } from "@ant-design/icons";
import { ROLE_LABEL_MAP, MODULE_LABEL_MAP } from "./constants";

export default function SubUserSummaryCard({
  name,
  email,
  role,
  roleLabelMap = ROLE_LABEL_MAP,
  moduleLabelMap = MODULE_LABEL_MAP,
  accountAccess = [],
  submitting = false,
  onSubmit,
  onCancel,
  submitLabel = "Send invite",
}) {
  const roleBadge = roleLabelMap[role] || "Sub-user";

  const enabledAccounts = useMemo(
    () => accountAccess.filter((a) => a.enabled),
    [accountAccess]
  );

  const totalModules = useMemo(
    () => enabledAccounts.reduce((sum, acc) => sum + (acc.modules?.length || 0), 0),
    [enabledAccounts]
  );

  const initials = name?.trim()
    ? name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const canSubmit =
    name?.trim() &&
    email?.trim() &&
    enabledAccounts.length > 0 &&
    enabledAccounts.every((a) => (a.modules || []).length > 0);

  return (
    <aside className="mu-summary-card mu-summary-card--embedded" aria-label="Invite summary">
      <div className="mu-summary-card__hero">
        <div className="mu-summary-card__avatar" aria-hidden>
          {initials}
        </div>
        <div className="mu-summary-card__hero-text">
          <div className="mu-summary-card__hero-title">{name?.trim() || "New user"}</div>
          <div className="mu-summary-card__hero-email">{email?.trim() || "no email yet"}</div>
        </div>
        <span className="mu-summary-card__role-pill">{roleBadge}</span>
      </div>

      <div className="mu-summary-card__stats">
        <div className="mu-summary-card__stat">
          <span className="mu-summary-card__stat-value">{enabledAccounts.length}</span>
          <span className="mu-summary-card__stat-label">Accounts</span>
        </div>
        <div className="mu-summary-card__stat-divider" aria-hidden />
        <div className="mu-summary-card__stat">
          <span className="mu-summary-card__stat-value">{totalModules}</span>
          <span className="mu-summary-card__stat-label">Modules</span>
        </div>
      </div>

      <div className="mu-summary-card__preview">
        <div className="mu-summary-card__preview-label">Selection preview</div>
        {enabledAccounts.length === 0 ? (
          <div className="mu-summary-card__preview-empty">
            Provide an account and pick modules to give the user access here.
          </div>
        ) : (
          <ul className="mu-summary-card__preview-list">
            {enabledAccounts.slice(0, 5).map((acc) => (
              <li key={acc.account_id} className="mu-summary-card__preview-row">
                <div className="mu-summary-card__preview-account">
                  <span className="mu-summary-card__preview-dot" aria-hidden />
                  <span className="text-truncate">{acc.account_name}</span>
                </div>
                {(acc.modules || []).length > 0 ? (
                  <div className="mu-summary-card__preview-modules">
                    {(acc.modules || []).slice(0, 4).map((mod) => (
                      <span key={`${acc.account_id}-${mod}`} className="mu-summary-card__module-tag">
                        {moduleLabelMap[mod] || mod}
                      </span>
                    ))}
                    {(acc.modules || []).length > 4 ? (
                      <span className="mu-summary-card__module-tag">
                        +{(acc.modules || []).length - 4}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <span className="mu-summary-card__preview-warn">Pick at least one module</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mu-summary-card__actions">
        <button
          type="button"
          className="btn btn-danger fw-bold w-100 mu-summary-card__submit"
          // disabled={submitting || !canSubmit}
          onClick={onSubmit}
        >
          {submitting ? (
            "Please wait…"
          ) : (
            <>
              <SendOutlined className="me-2" />
              {submitLabel}
            </>
          )}
        </button>
        <button type="button" className="btn btn-light mu-summary-card__cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </aside>
  );
}
